import { Controller } from "@hotwired/stimulus"

// Preloaded 1×1 transparent GIF used to hide the native drag ghost for
// items that manifest a live preview on the canvas (text, shapes).
const TRANSPARENT_DRAG_IMAGE = new Image()
TRANSPARENT_DRAG_IMAGE.src =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"

/**
 * Micro-controller for selectable/draggable items inside toolbox panels.
 *
 * Dispatches a "toolbox-item:selected" event on click that bubbles up
 * to the provider (fabric or composition editor).
 *
 * Un item qui vient d'être uploadé (uploadBatch, posé par la réponse turbo-stream) s'annonce
 * une fois à sa connexion — "toolbox-item:uploaded", avec le lot de son dépôt : la toolbox,
 * qui a figé la cible de ce lot, le pose. L'attribut tombe aussitôt, un morph du frame ne
 * le rejoue pas.
 *
 * Usage:
 *   <div data-controller="toolbox-item"
 *        data-toolbox-item-type-value="media"
 *        data-toolbox-item-payload-value='{"id":1,"url":"…"}'
 *        data-action="click->toolbox-item#select dragstart->toolbox-item#dragstart"
 *        draggable="true">
 */
export default class extends Controller {
  static values = {
    type: String,    // "text", "shape", "media", "variable"
    payload: Object, // Arbitrary data passed to the provider
    uploadBatch: String,
  }

  connect() {
    if (!this.uploadBatchValue) return;

    const batch = this.uploadBatchValue;
    this.element.removeAttribute(`data-${this.identifier}-upload-batch-value`);
    this.element.dispatchEvent(
      new CustomEvent("toolbox-item:uploaded", {
        detail: { batch, item: { type: this.typeValue, ...this.payloadValue } },
        bubbles: true,
      })
    );
  }

  select() {
    this.element.dispatchEvent(
      new CustomEvent("toolbox-item:selected", {
        detail: { type: this.typeValue, ...this.payloadValue },
        bubbles: true,
      })
    )
  }

  dragstart(e) {
    const data = JSON.stringify({ type: this.typeValue, ...this.payloadValue })
    e.dataTransfer.setData("application/x-toolbox-item", data)
    e.dataTransfer.effectAllowed = "copy"

    // Canvas-bound items get a custom preview (HTML ghost on the way, live
    // Fabric object once over the canvas) — hide the browser ghost so it
    // doesn't double it. (setDragImage can only be called here, not mid-drag.)
    // Keep this list in sync with the toDragPayload switches in
    // fabric_controller.ts and composition_editor/fabric_controller.js.
    if (["text", "shape", "media", "userSlot"].includes(this.typeValue)) {
      e.dataTransfer.setDragImage(TRANSPARENT_DRAG_IMAGE, 0, 0)
    }

    // Broadcast payload so the editor can arm puppet drag immediately
    // (dataTransfer content is not readable during dragover, only types are)
    this.element.dispatchEvent(
      new CustomEvent("toolbox-item:dragstart", {
        detail: { type: this.typeValue, ...this.payloadValue },
        bubbles: true,
      })
    )
  }
}
